import React, { useState } from 'react';
import { graphql } from 'gatsby';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import toUpper from 'lodash/toUpper';
import capitalize from 'lodash/capitalize';
import toLower from 'lodash/toLower';
import upperFirst from 'lodash/upperFirst';

// import { Form, FormGroup, Label, Input } from 'reactstrap';

import Template2Cols from '../components/template2Cols';
import ErrorMessage from '../components/ErrorMessage';
import SuccessMessage from '../components/SuccessMessage';
import SafeMarkdown from '../components/SafeMarkdown';
import SafeGatsbyImage from '../components/SafeGatsbyImage';
import { renderPhoneNumber } from '../lib/renderHelper';
import { removeExtraSpaces } from '../lib/utils';
import { handleData } from '../lib/dataHelper';

const rePhoneNumber = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;

const NousContacter = ({ data }) => {
  const [successMsg, setSuccessMsg] = useState();
  const dataToUse = handleData(data) || {};
  const {
    postalAddress,
    schedules,
    phoneNumber,
    partenaireId,
    staticMap,
    title,
    gpsLatitude = undefined,
    gpsLongitude = undefined,
  } = dataToUse;
  const { label: phoneNumberLabel = '', value: phoneNumberValue = '' } = renderPhoneNumber(phoneNumber);

  const validationSchema = yup.object().shape({
    nom: yup.string().required('Merci de renseigner un nom.'),
    email: yup.string().email('Merci de renseigner un email valide').required('Merci de renseigner un email.'),
    codePostal: yup.string().required('Merci de renseigner un code postal.'),
    ville: yup.string().required('Merci de renseigner une ville.'),
    telephone: yup
      .string()
      .test('phone', 'Merci de renseigner un téléphone valide.', (value) => rePhoneNumber.test(value))
      .required('Merci de renseigner un téléphone.'),
    workemail: yup.string().test('honeypot', 'Merci de laisser ce champ vide', (value) => /^$/.test(value)),
    // recaptcha: TODO: Google Recaptcha
  });

  const prepareData = (data = {}) => {
    const { nom, prenom, email, ville, pays, ...others } = Object.entries(data).reduce(
      (acc, [k, v]) => ({ ...acc, [k]: removeExtraSpaces(v) }),
      {},
    );

    return {
      partenaire: partenaireId,
      nom: toUpper(nom),
      prenom: upperFirst(prenom),
      email: toLower(email),
      ville: toUpper(ville),
      pays: toUpper(pays),
      ...others,
    };
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm({ resolver: yupResolver(validationSchema) });

  const onSubmit = async (data) => {
    // console.debug('Submitting...', data);
    // console.debug('Submitted to:', `${process.env.GATSBY_STRAPI_URL}/contacts`);

    const init = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(prepareData(data)),
    };

    return fetch(`${process.env.GATSBY_STRAPI_URL}/contacts`, init)
      .then((resp) => {
        if (!resp.ok) {
          throw new Error('40X error');
        }
        setSuccessMsg('Nous avons bien reçu votre message. Votre demande sera traitée rapidement.');
        return resp.json();
      })
      .catch(({ status }) => {
        let message;
        switch (status) {
          case 403:
            message = 'Opération non autorisée';
            break;
          default:
            message = "La demande n'a pu aboutir. Merci de réessayer à nouveau.";
        }

        setError('general', {
          type: 'manual',
          message,
        });
      });
  };

  const OnFormFocus = () => {
    clearErrors('general');
    setSuccessMsg();
  };
  const submitHandler = (e) => {
    e.preventDefault();
  };

  return (
    <Template2Cols pageTitle="Nous contacter" data={dataToUse}>
      <>
        <div className="col-md-7">
          <ErrorMessage errors={errors} name="general" />
          <SuccessMessage message={successMsg} />
          <form className="form-light mt-20" onSubmit={submitHandler} onFocus={OnFormFocus}>
            <div className="row ">
              <div className="col-md-12">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    name="raisonSociale"
                    placeholder="Raison sociale"
                    {...register('raisonSociale')}
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-4">
                <div className="form-group">
                  <select className="form-control" name="civilite" id="civilite" {...register('civilite')}>
                    <option disabled="disabled" value="Civilite">
                      Civilité
                    </option>
                    <option value="M">M</option>
                    <option value="MME">MME</option>
                    <option value="MLLE">MLLE</option>
                    <option value="M_MME">M MME</option>
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <input type="text" className="form-control" name="nom" placeholder="Nom" {...register('nom')} />
                  <ErrorMessage errors={errors} name="nom" />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    name="prenom"
                    placeholder="Prénom"
                    {...register('prenom')}
                  />
                </div>
              </div>
            </div>
            <div className="row ">
              <div className="col-md-12">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    name="adresse"
                    placeholder="Adresse"
                    {...register('adresse')}
                  />
                </div>
              </div>
            </div>
            <div className="row ">
              <div className="col-md-12">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    name="complementAdresse"
                    placeholder="Complément d'adresse"
                    {...register('complementAdresse')}
                  />
                </div>
              </div>
            </div>
            <div className="row ">
              <div className="col-md-4">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    name="codePostal"
                    placeholder="Code postal"
                    {...register('codePostal')}
                  />
                  <ErrorMessage errors={errors} name="codePostal" />
                </div>
              </div>
              <div className="col-md-8">
                <div className="form-group">
                  <input type="text" className="form-control" name="ville" placeholder="Ville" {...register('ville')} />
                  <ErrorMessage errors={errors} name="ville" />
                </div>
              </div>
            </div>
            <div className="row ">
              <div className="col-md-12">
                <div className="form-group">
                  <input type="text" className="form-control" name="pays" placeholder="Pays" {...register('pays')} />
                </div>
              </div>
            </div>
            <div className="row ">
              <div className="col-md-7">
                <div className="form-group">
                  <input type="text" className="form-control" name="email" placeholder="Email" {...register('email')} />
                  <ErrorMessage errors={errors} name="email" />
                </div>
              </div>
              <div className="col-md-5">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    name="telephone"
                    placeholder="Téléphone (portable de préf.)"
                    {...register('telephone')}
                  />
                  <ErrorMessage errors={errors} name="telephone" />
                </div>
              </div>
            </div>

            <div className="row _notdiv">
              <div className="col-md-12">
                <div className="form-group">
                  <textarea
                    className="form-control"
                    name="commentaires"
                    placeholder="Votre demande"
                    {...register('commentaires')}
                  ></textarea>
                </div>
              </div>
            </div>
            <div className="row workemail">
              <div className="col-md-12">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    name="workemail"
                    placeholder="Merci de laisser ce champ vide"
                    {...register('workemail')}
                  />
                  <ErrorMessage errors={errors} name="workemail" />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12"></div>
            </div>
            <div className="row">
              <div className="col-md-12">
                <input
                  type="submit"
                  className="btn btn-two"
                  name="contactsubmit"
                  value="Envoyer"
                  onClick={handleSubmit(onSubmit)}
                />
              </div>
            </div>
          </form>
        </div>

        <div className="col-md-5">
          {gpsLatitude && gpsLongitude && (
            <a
              href={staticMap.mapUrl}
              itemProp="hasMap"
              title={`Google Maps for ${title}`}
              target="_blank"
              rel="noreferrer"
            >
              <SafeGatsbyImage image={staticMap} />
            </a>
          )}
          <div className="row">
            <div className="col-sm-6">
              <div className="subsection">
                <h2 className="section-title">Nos coordonnées</h2>
                <div className="contact-info hierarchy">
                  {postalAddress && (
                    <>
                      <h3>Adresse</h3>
                      {title}
                      <SafeMarkdown itemProp="address">{postalAddress}</SafeMarkdown>
                    </>
                  )}

                  {phoneNumberValue && (
                    <>
                      <h3>Téléphone</h3>
                      <p>
                        {phoneNumberValue && (
                          <a href={`tel:${phoneNumberValue}`} itemProp="telephone" title="Nous telephoner">
                            {phoneNumberLabel}
                          </a>
                        )}
                      </p>
                    </>
                  )}

                  {gpsLatitude && gpsLongitude && (
                    <>
                      <h3>Coordonnées GPS</h3>
                      <a
                        href={staticMap.mapUrl}
                        title={`Google Maps for ${title}`}
                        target="_blank"
                        rel="noreferrer"
                        itemProp="geo"
                        itemScope
                        itemType="https://schema.org/GeoCoordinates"
                      >
                        <span itemProp="latitude" content={gpsLatitude}>
                          {gpsLatitude}
                        </span>
                        ,&nbsp;
                        <span itemProp="longitude" content={gpsLongitude}>
                          {gpsLongitude}
                        </span>
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* SCHEDULES */}
            {schedules && (
              <div className="col-sm-6">
                <div className="subsection">
                  <h2 className="section-title">Horaires d'ouverture</h2>
                  <SafeMarkdown
                    itemProp="openingHours"
                    content="Mo-Fr 08:00-12:00,14:00-17:00"
                    className="contact-info"
                  >
                    {schedules}
                  </SafeMarkdown>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    </Template2Cols>
  );
};

export const query = graphql`
  query NousContacterQuery($partenaireId: Int!) {
    staticMap {
      ...Map
    }
    strapiPartenaires(strapiId: { eq: $partenaireId }) {
      partenaireId: strapiId
      ...Contact
      ...Logo
      ...Header
      ...Footer
    }
  }
`;

export default NousContacter;
